import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";
import type { Task } from "@recobra/schemas/task";

import { caseSummaryQueryOptions } from "../../cases";
import { tasksApi } from "../../lib/api";
import { getErrorMessage } from "../../lib/api";
import { extendForm } from "../../lib/form";
import { useTaskTypes } from "../task-data";
import {
  buildCreateTaskInput,
  dvcSearchDetailsErrors,
  taskFormDefaultValues,
} from "./TaskFormValues";

export type TaskFormContext = {
  contextType: "case" | "client";
  contextId: string;
  contextStage?: string;
};

export type TaskCreationFormOptions = {
  context: TaskFormContext;
  initialTaskType?: string;
  onOpenChange: (open: boolean) => void;
  onSaved?: (task: Task) => void;
};

const taskForm = extendForm({});

/** The shared form instance behind both task creation and inline metadata editing. */
export const { useAppForm: useTaskForm } = taskForm;

export const useTaskCreationForm = ({
  context,
  initialTaskType,
  onOpenChange,
  onSaved,
}: TaskCreationFormOptions) => {
  const queryClient = useQueryClient();
  const { data: kase } = useQuery({
    ...caseSummaryQueryOptions(queryClient, context.contextId),
    enabled: context.contextType === "case" && !!context.contextId,
  });

  const types = useTaskTypes();
  const systemOf = (taskType: string) => types.data?.get(taskType)?.system ?? null;
  const form = useTaskForm({
    defaultValues: {
      ...taskFormDefaultValues,
      taskType: initialTaskType ?? "",
    },
    validators: {
      onSubmit: ({ value }) => {
        const fields: Record<string, string> | undefined = dvcSearchDetailsErrors(
          value,
          systemOf(value.taskType),
          kase?.incident?.date,
        );
        return fields ? { fields } : undefined;
      },
    },
    onSubmit: async ({ value }) => {
      try {
        if (!value.taskType) return;
        const task = await tasksApi.createTask(
          buildCreateTaskInput(value, value.taskType, systemOf(value.taskType), context),
        );
        if (!task) {
          toast.info(m.task_create_duplicate_open());
        } else {
          toast.success(m.task_created_success());
          queryClient.setQueryData(["tasks", task.id], task);
          onSaved?.(task);
        }
        void queryClient.invalidateQueries({ queryKey: ["tasks"] });
        form.reset();
        onOpenChange(false);
      } catch (error) {
        toast.error(m.task_create_error({ message: getErrorMessage(error) }));
      }
    },
  });

  const onModalOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
    }
    onOpenChange(open);
  };

  return { form, context, onModalOpenChange };
};

export type TaskCreationFormApi = ReturnType<typeof useTaskCreationForm>["form"];
